import { isAddress } from 'web3-utils'
import { INTERFACE_IDS } from '@lukso/lsp-smart-contracts'
import ERC725 from '@erc725/erc725.js'
import { URLDataWithHash } from '@erc725/erc725.js/build/main/src/types'

import { PopupDatabase } from '@/popup/storage/Database'
import { LSP3_PROFILE } from '@/shared/config'
import useErc725 from '@/popup/compositions/useErc725'
import { getProfileImages } from '@/popup/utils/getProfileImages'
import { logError, logInfo } from '@/shared/logger'
import { useWeb3Eth } from '@/popup/compositions/useWeb3Eth'
import { checkInterface } from '@/shared/checkInterface'
import {
  UpAddress,
  EoaAddress,
  PopupProfile,
  FrontendProfile,
} from '@/shared/models/Profile'
import { t } from '@/popup/lib/i18n'

export const loadCachedPopupProfile = async (
  address: UpAddress | EoaAddress
): Promise<PopupProfile | undefined> => {
  if (!isAddress(address)) {
    return
  }

  return await PopupDatabase.getPopupProfile(address)
}

export const updatePopupProfile = async (
  popupProfile: PopupProfile
): Promise<PopupProfile> => {
  const LSP3Profile = await fetchLatestProfile({
    ...popupProfile.saved,
    address: popupProfile.address,
  })

  if (LSP3Profile) {
    await PopupDatabase.updatePopupProfile(
      popupProfile.address,
      'saved',
      LSP3Profile
    )
  }

  return (await PopupDatabase.getPopupProfile(
    popupProfile.address
  )) as PopupProfile
}

export const createPopupProfile = async (
  address: UpAddress | EoaAddress
): Promise<PopupProfile | undefined> => {
  if (!isAddress(address)) {
    return
  }

  const popupProfile: PopupProfile = {
    address,
    temporary: undefined,
    createdAt: Date.now(),
  }
  const web3Eth = useWeb3Eth().getWeb3Eth()

  if (await checkInterface(web3Eth, address, INTERFACE_IDS.LSP0ERC725Account)) {
    const LSP3Profile = await fetchLatestProfile({ address })
    popupProfile.saved = LSP3Profile
  } else {
    const EoAProfile = { address }
    popupProfile.saved = EoAProfile
  }

  await PopupDatabase.insertPopupProfile(popupProfile)

  return popupProfile
}

export const getLSP3ProfileAndImages = async (erc725: ERC725) => {
  try {
    const fetchData = await erc725.fetchData(LSP3_PROFILE)
    //@ts-ignore
    const LSP3Profile = fetchData['value'][LSP3_PROFILE] as LSP3ProfileMetadata
    const { coverPicture, profilePicture } = await getProfileImages({
      ...(LSP3Profile?.backgroundImage
        ? { backgroundImage: LSP3Profile?.backgroundImage }
        : {}),
      ...(LSP3Profile?.profileImage
        ? { profileImage: LSP3Profile?.profileImage }
        : {}),
    })
    return {
      coverPicture,
      profilePicture,
      LSP3Profile,
    }
  } catch (error) {
    logError(`Error while getting LSP3 profile and images:`, error)
    return { error }
  }
}

export const fetchLatestProfile = async (
  profile: FrontendProfile
): Promise<FrontendProfile | undefined> => {
  try {
    const erc725 = useErc725().getErc725(profile.address)
    if (!erc725 || !erc725.getData) {
      // Due to timing during shutdown of vue in jest this
      // sometimes is undefined
      return undefined
    }
    const data = await erc725.getData(LSP3_PROFILE)

    const urlDataWithHash = data.value as URLDataWithHash
    if (!urlDataWithHash) {
      return undefined
    }

    const { verification } = urlDataWithHash

    if (verification.data === profile.verification?.data) {
      logInfo('fetching profile: profile hash the same; skipping')
      return undefined
    }

    logInfo('fetching profile: erc725 fetchData:', data)

    const { LSP3Profile, coverPicture, profilePicture } =
      await getLSP3ProfileAndImages(erc725)

    const upsertProfile: FrontendProfile = {
      address: profile.address,
      backgroundImage: LSP3Profile?.backgroundImage,
      profileImage: LSP3Profile?.profileImage,
      name: LSP3Profile?.name,
      description: LSP3Profile?.description,
      links: LSP3Profile?.links,
      tags: LSP3Profile?.tags,
      coverPicture,
      profilePicture,
      verification,
      coverPictureDirty: false,
      profilePictureDirty: false,
    }
    return upsertProfile
  } catch (error) {
    logError(`Error while fetching latest profile:`, error)
    throw new Error(t('errors.invalid_up_profile_components'))
  }
}
